'use client';

import { useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AddressFields } from '@sola/ark-core/molecules/address-fields/address-fields.tsx';
import {
  Dialog,
  DialogActionClose,
  DialogActionPrimary,
  DialogActions,
  DialogContent,
  DialogTrigger,
} from '@sola/ark-core/molecules/dialog/dialog.tsx';
import { Form, useFormMethods } from '@sola/ark-core/molecules/form/form.tsx';
import { useModalBusy } from '@sola/ark-core/molecules/modal/modal.tsx';
import { useAddressUpdate } from '@sola/my-sdk/settings/hooks.ts';
import { CheckoutAddressTypeEnum } from '@sola/schema/dotcom/checkout/contactInfo.ts';
import type { MailingAddress as MailingAddressModal } from '@sola/schema/dotcom/shared/address.ts';
import { MailingAddressEnum } from '@sola/schema/dotcom/shared/address.ts';
import { mapCountryNameToCode } from '@sola/schema/dotcom/shared/geo.ts';
import type { GraphQLSupporterAddress } from '@sola/schema/dotcom/supporters/schema.generated.ts';

import { useLocalAlerts } from '../../molecules/local-alerts/local-alerts-provider.tsx';
import {
  SettingsErrorAlertMessage,
  createAddressUpdateSuccessAlert,
} from '../../utils/alerts.tsx';
import { mapAddressDetails } from '../../utils/map-address-Details.ts';

const MailingModalForm = ({
  defaultFormValues,
  onSuccess,
}: {
  defaultFormValues: MailingAddressModal;
  onSuccess: () => void;
}) => {
  const { mutateAsync: updateMailingAddress } = useAddressUpdate();
  const [isBusy, setIsBusy] = useState(false);
  const { t } = useTranslation(['core', 'my-settings']);
  const formMethods = useFormMethods();
  useModalBusy(isBusy);
  const country = mapCountryNameToCode(
    mapCountryNameToCode(defaultFormValues[MailingAddressEnum.Country]),
  );
  const handleSubmit = async (data: FieldValues) => {
    setIsBusy(true);
    formMethods.current?.clearErrors();
    try {
      await updateMailingAddress({
        address: mapAddressDetails(data as MailingAddressModal),
      });
      onSuccess();
    } catch {
      formMethods.current?.setGeneralError({
        message: (
          <SettingsErrorAlertMessage i18nKey="my-settings:addressUpdate.error.message" />
        ),
      });
      await formMethods.current?.trigger();
      setIsBusy(false);
    }
  };

  return (
    <Form
      onSubmit={handleSubmit}
      formMethods={formMethods}
      options={{
        defaultValues: {
          ...defaultFormValues,
          [MailingAddressEnum.Country]: country,
        },
        mode: 'onChange',
      }}
    >
      <AddressFields
        addressType={CheckoutAddressTypeEnum.Mailing}
        defaultCountry={country}
      />
      <DialogActions>
        <DialogActionClose>{t('core:actions.cancel')}</DialogActionClose>
        <DialogActionPrimary type="submit" isBusy={isBusy} disableUntilFilled>
          {t('core:actions.save')}
        </DialogActionPrimary>
      </DialogActions>
    </Form>
  );
};

export const SettingsModalMailing = ({
  addressDetails,
  openTrigger,
}: {
  addressDetails: GraphQLSupporterAddress;
  openTrigger: React.ReactElement<HTMLButtonElement>;
}) => {
  const sectionAlerts = useLocalAlerts();
  const [isOpen, setIsOpen] = useState(false);

  const defaultFormValues: MailingAddressModal = {
    [MailingAddressEnum.Address]: addressDetails?.street ?? '',
    [MailingAddressEnum.Address2]: addressDetails?.street2 ?? '',
    [MailingAddressEnum.City]: addressDetails?.city ?? '',
    [MailingAddressEnum.Country]: addressDetails?.country ?? '',
    [MailingAddressEnum.State]: addressDetails?.state ?? '',
    [MailingAddressEnum.Postal]: addressDetails?.postalCode ?? '',
  };

  const { t } = useTranslation(['core', 'my-settings']);

  const handleSuccess = () => {
    sectionAlerts.addAlerts(createAddressUpdateSuccessAlert());
    setIsOpen(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setIsOpen(nextOpen);
    sectionAlerts.clearAlerts();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger>{openTrigger}</DialogTrigger>
      <DialogContent icon="mail-01" title={t('my-settings:form.mailing.title')}>
        <MailingModalForm
          defaultFormValues={defaultFormValues}
          onSuccess={handleSuccess}
        />
      </DialogContent>
    </Dialog>
  );
};
